import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {deleteSpell, getSpell, saveSpell, searchSpells, SpellFilter} from "@/spells/SpellApi.ts";

export const spellKeys = {
    all: ["spells"] as const,
    lists: () => [...spellKeys.all, "list"] as const,
    list: (filter: SpellFilter, pageSize: number) => [...spellKeys.lists(), filter, pageSize] as const,
    detail: (id: string) => [...spellKeys.all, "detail", id] as const,
};

export function useSpellQuery(id?: string) {
    return useQuery({
        queryKey: spellKeys.detail(id ?? ""),
        queryFn: ({signal}) => getSpell(id!, signal),
        enabled: Boolean(id),
    });
}

export function useSpellsQuery(filter: SpellFilter, pageSize = 50) {
    const query = useInfiniteQuery({
        queryKey: spellKeys.list(filter, pageSize),
        initialPageParam: 0,
        queryFn: ({pageParam, signal}) => searchSpells({page: pageParam, size: pageSize}, filter, signal),
        getNextPageParam: lastPage => {
            const nextPage = lastPage.page.number + 1;
            return nextPage < lastPage.page.totalPages ? nextPage : undefined;
        },
    });
    const spells = query.data?.pages.flatMap(page => page.content) ?? [];

    return {
        ...query,
        spells,
        totalCount: query.data?.pages[0]?.page.totalElements ?? 0,
        rowCount: spells.length + (query.hasNextPage ? 1 : 0),
        loadMore: async () => {
            if (query.hasNextPage) await query.fetchNextPage({cancelRefetch: false});
        },
    };
}

export function useDeleteSpellMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteSpell,
        onSuccess: (_, id) => {
            queryClient.removeQueries({queryKey: spellKeys.detail(id)});
            void queryClient.invalidateQueries({queryKey: spellKeys.lists()});
        },
    });
}

export function useSaveSpellMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: saveSpell,
        onSuccess: spell => {
            queryClient.setQueryData(spellKeys.detail(spell.id), spell);
            void queryClient.invalidateQueries({queryKey: spellKeys.lists()});
        },
    });
}
